import { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, LockOpenIcon, PencilSquareIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@follow/ui';
import moment from 'moment';
import toast from 'react-hot-toast';
import { AvailableEquipment, Rental, RentalStatus, PaymentMethod } from '../../../services/models/rental';
import { Equipment } from '../../../services/models/assets';
import RentalService from '../../../services/RentalService';
import StripeService from '../../../services/StripeService';
import AssetsService from '../../../services/AssetsService';
import ModalConfirmDelete from '../../../components/Modals/ModalConfirmDelete';
import ModalOpenLock from '../../../components/Modals/ModalOpenLock';
import ModalExtendRental from '../../../components/Modals/ModalExtendRental';
import RentalEquipmentPicker from './RentalEquipmentPicker';
import RentalHistoryTable from './RentalHistoryTable';
import { getEquipmentCodeToken, getEquipmentLockLabel, getRentalStatusInfo } from '../utils/rentalUtils';

interface RentalDetailsModalProps {
  rental?: Rental;
  stationToken?: string;
  stationId: number;
  discountPercentage?: number;
  visible: boolean;
  onClose: () => void;
  // Called with the fresh rental after a successful extend, so the still-open modal can
  // reflect the new price/duration immediately — the list/history queries it also
  // invalidates don't feed this modal's `rental` prop back (it's a one-time snapshot).
  onRentalUpdated?: (rental: Rental) => void;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 items-baseline">
    <span className="typo-small text-neutral-50">{label}</span>
    <span className="typo-small text-neutral text-right">{children}</span>
  </div>
);

const RentalDetailsModal: React.FC<RentalDetailsModalProps> = ({ rental, stationToken, stationId, discountPercentage, visible, onClose, onRentalUpdated }) => {
  const queryClient = useQueryClient();
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editStartDate, setEditStartDate] = useState<string>();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [pickerEquipmentId, setPickerEquipmentId] = useState<number | null>(null);
  const [equipmentToOpen, setEquipmentToOpen] = useState<Equipment | null>(null);
  const [applyReferralDiscount, setApplyReferralDiscount] = useState(false);
  // Cash "charge and close": editable amount (euros) + optional deposit.
  const [cashAmount, setCashAmount] = useState<string>('');
  const [includeDeposit, setIncludeDeposit] = useState(false);
  const [confirmExtendOpen, setConfirmExtendOpen] = useState(false);

  useEffect(() => {
    if (visible) {
      setIsEditingDate(false);
      setEditStartDate(undefined);
      setConfirmCancel(false);
      setPickerEquipmentId(null);
      setEquipmentToOpen(null);
      setApplyReferralDiscount(false);
      setCashAmount(rental ? (rental.price / 100).toFixed(2) : '');
      setIncludeDeposit(false);
      setConfirmExtendOpen(false);
    }
  }, [visible, rental?.id, rental?.price]);

  const status = rental ? getRentalStatusInfo(rental.rentalStatus) : null;

  // Status history (audit trail) — fetched on open; the list payloads stay light.
  const { data: events } = useQuery({
    queryKey: ['rental-details', rental?.id],
    queryFn: () => RentalService.getRental(rental!.id),
    // id > 0 skips the dev-only fake rentals (negative ids, nothing to fetch)
    enabled: visible && !!rental && rental.id > 0,
    select: (data) => data.rental.events ?? [],
  });

  // Extend options (max hours addable, bounded by deposit headroom and equipment
  // availability) — only fetched once the confirm modal is opened.
  const extendOptionsQuery = useQuery({
    queryKey: ['rental-extend-options', rental?.id],
    queryFn: () => RentalService.getExtendOptions(rental!.id),
    enabled: confirmExtendOpen && !!rental && rental.id > 0,
  });

  // Station events (keypad codes, lock opens, hook states) tied to the rental —
  // merged with the status history into the Historique table.
  const { data: stationEvents } = useQuery({
    queryKey: ['rental-station-events', rental?.id],
    queryFn: () => RentalService.getRentalStationEvents(rental!.id),
    enabled: visible && !!rental && rental.id > 0,
    select: (data) => data.events ?? [],
  });

  const duration = useMemo(() => {
    if (!rental) return '';
    const start = moment.utc(rental.startDateUTC);
    const end = moment.utc(rental.endDateUTC);
    if (end.isBefore(start)) return 'Annulé';
    if (end.diff(start, 'days') === 0) return `${end.diff(start, 'hours')} heures`;
    return `${end.diff(start, 'days')} jours`;
  }, [rental]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rentals', stationId] });
  };

  const endRentalMutation = useMutation({
    mutationFn: (rentalId: number) => RentalService.endRental(rentalId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Réservation annulée');
        invalidate();
        onClose();
      } else {
        toast.error('Erreur lors de l\'annulation');
      }
    },
    onError: () => toast.error('Une erreur est survenue'),
  });

  const closeRentalMutation = useMutation({
    mutationFn: (rentalId: number) => RentalService.closeRental(rentalId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Réservation fermée');
        invalidate();
        onClose();
      } else {
        toast.error('Erreur lors de la fermeture');
      }
    },
    onError: () => toast.error('Une erreur est survenue'),
  });

  const capturePaymentMutation = useMutation({
    mutationFn: (rentalId: number) => {
      if (rental?.invoice?.paymentMethod === PaymentMethod.CASH) {
        return RentalService.chargeCashAndClose(rentalId, {
          amount: Math.round(Number(cashAmount || 0) * 100),
          includeDeposit,
        });
      }
      return StripeService.capturePayment(rentalId, applyReferralDiscount);
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success(
          rental?.invoice?.paymentMethod === PaymentMethod.CASH
            ? 'Encaissement enregistré et réservation fermée'
            : 'Paiement capturé et réservation fermée',
        );
        invalidate();
        onClose();
      } else {
        toast.error('Erreur lors de la facturation');
      }
    },
    onError: () => toast.error('Erreur lors de la facturation'),
  });

  const updateStartDateMutation = useMutation({
    mutationFn: ({ rentalId, startDate }: { rentalId: number; startDate: string }) =>
      RentalService.updateRentalStartDateUTC(rentalId, startDate),
    onSuccess: () => {
      toast.success('Date modifiée, mail envoyé au client');
      setIsEditingDate(false);
      invalidate();
    },
    onError: () => toast.error('Une erreur est survenue'),
  });

  const changeEquipmentMutation = useMutation({
    mutationFn: ({ rentalId, previousEquipmentId, newEquipmentId, updatePrice }: { rentalId: number; previousEquipmentId: number; newEquipmentId: number; updatePrice: boolean }) =>
      RentalService.changeRentalEquipment(rentalId, previousEquipmentId, newEquipmentId, updatePrice),
    onSuccess: (updatedRental) => {
      toast.success('Équipement remplacé');
      setPickerEquipmentId(null);
      onRentalUpdated?.(updatedRental);
      queryClient.invalidateQueries({ queryKey: ['rental-available-equipments'] });
      invalidate();
    },
    onError: (err) => toast.error((err as { message?: string })?.message ?? 'Erreur lors du remplacement'),
  });

  const extendRentalMutation = useMutation({
    mutationFn: ({ rentalId, additionalHours }: { rentalId: number; additionalHours: number }) =>
      RentalService.extendRental(rentalId, additionalHours),
    onSuccess: (updatedRental, { additionalHours }) => {
      toast.success(`Réservation prolongée de ${additionalHours}h`);
      setConfirmExtendOpen(false);
      onRentalUpdated?.(updatedRental);
      invalidate();
    },
    // Service.fetch throws the parsed API error body ({ message, status }), not an Error instance
    onError: (err) => toast.error((err as { message?: string })?.message ?? 'Erreur lors de la prolongation'),
  });

  const openLockMutation = useMutation({
    mutationFn: (lockId: number) => {
      if (!stationToken) throw new Error('No station token');
      return AssetsService.openLock(stationToken, lockId);
    },
    onSuccess: () => {
      toast.success('Ouverture envoyée');
      setEquipmentToOpen(null);
    },
    onError: () => toast.error('Erreur lors de l\'ouverture'),
  });

  if (!rental || !status) return null;

  const isNotStarted = rental.rentalStatus === RentalStatus.NOT_STARTED;
  const isCancellable = [RentalStatus.NOT_STARTED, RentalStatus.WAITING_PAYMENT].includes(rental.rentalStatus);
  const isClosable = rental.rentalStatus !== RentalStatus.CLOSED;
  const isCapturing = capturePaymentMutation.isPending;

  // Display-only preview: the API recomputes the discounted amount from the
  // station's discount_percentage when applyReferralDiscount is sent.
  const discount = discountPercentage ?? 15;
  const captureAmount = applyReferralDiscount
    ? Math.round(rental.price * (100 - discount) / 100)
    : rental.price;

  const isCash = rental.invoice?.paymentMethod === PaymentMethod.CASH;
  // Preview of the amount recorded as charged for a cash rental (in cents).
  const cashChargeCents = Math.round(Number(cashAmount || 0) * 100) + (includeDeposit ? rental.deposit : 0);

  // A Stripe-paid rental, not yet captured, with a whole-hour duration, in NOT_STARTED/STARTED/
  // RETURNED — the API enforces the same conditions and will 400/409 with a specific reason otherwise.
  const durationHours = moment.utc(rental.endDateUTC).diff(moment.utc(rental.startDateUTC), 'hours', true);
  const isExtendable = !isCash
    && rental.invoice?.status === 'PRE_AUTHORIZED'
    && [RentalStatus.RENTED, RentalStatus.NOT_STARTED, RentalStatus.RETURNED].includes(rental.rentalStatus)
    && Number.isInteger(durationHours);

  // Mirrors the API's own eligibility gate for updating price on an equipment
  // swap (rental.service.ts updateRentalEquipment): NOT_STARTED Stripe
  // pre-authorized rentals only, since only those have a live authorized-amount
  // ceiling to validate a price change against.
  const isSwapPriceEligible = !isCash && rental.invoice?.status === 'PRE_AUTHORIZED' && isNotStarted;

  const saveDate = () => {
    if (!editStartDate) return;
    // editStartDate is a UTC wall-clock string without offset marker: send full
    // ISO so the server doesn't depend on its own timezone to interpret it
    updateStartDateMutation.mutate({ rentalId: rental.id, startDate: moment.utc(editStartDate).toISOString() });
  };

  const hasEmergencyContact = rental.user.sosFirstname || rental.user.sosLastname || rental.user.sosPhone;

  // A nested modal (portaled outside this dialog's panel) registers its clicks as
  // outside-clicks here — keep this dialog open while one is showing.
  const handleClose = () => {
    if (equipmentToOpen || confirmCancel || pickerEquipmentId != null || confirmExtendOpen) return;
    onClose();
  };

  return (
    <>
      <Transition.Root show={visible} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-30 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between gap-4 px-6 pt-5 pb-4 border-b border-neutral-10">
                    <div className="flex items-center gap-3">
                      <Dialog.Title as="h3" className="typo-label text-neutral">
                        Réservation #{rental.publicId}
                      </Dialog.Title>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md typo-caption font-medium ${status.badgeClassName}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${status.dotClassName}`} />
                        {status.label}
                      </span>
                    </div>
                    <button type="button" onClick={onClose} className="text-neutral-40 hover:text-neutral transition-colors">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="px-6 py-5 flex flex-col gap-5 max-h-[60vh] overflow-y-auto">
                    {/* Equipments */}
                    <div className="flex flex-col gap-2">
                      {rental.equipments?.map((equipment) => {
                        const codeToken = getEquipmentCodeToken(rental, equipment);
                        const lockLabel = getEquipmentLockLabel(equipment);
                        return (
                        <div key={equipment.id} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <img src={equipment.imageUrl} alt={equipment.model} className="h-10 w-10 rounded-md object-cover flex-shrink-0" />
                            {codeToken && (
                              <span className="typo-caption font-semibold text-neutral px-1.5 py-0.5 rounded-md bg-neutral-5 outline outline-1 outline-offset-[-1px] outline-neutral-10 flex-shrink-0">
                                {codeToken}
                              </span>
                            )}
                            <div className="flex flex-col min-w-0">
                              <span className="typo-small text-neutral truncate">{equipment.model}</span>
                              {lockLabel && (
                                <span className="typo-caption text-neutral-40">{lockLabel}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEquipmentToOpen(equipment)}
                              disabled={!equipment.lockId}
                              icon={<LockOpenIcon className="h-4 w-4" />}
                            >
                              Ouvrir
                            </Button>
                            {isNotStarted && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPickerEquipmentId(equipment.id)}
                                icon={<ArrowsRightLeftIcon className="h-4 w-4" />}
                              >
                                Changer
                              </Button>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>

                    {/* Dates */}
                    <div className="flex flex-col gap-2 pt-4 border-t border-neutral-10">
                      <div className="flex justify-between gap-4 items-center">
                        <span className="typo-small text-neutral-50">Début</span>
                        {isEditingDate ? (
                          <input
                            type="datetime-local"
                            value={moment.utc(editStartDate).local().format('YYYY-MM-DDTHH:mm')}
                            onChange={(e) => setEditStartDate(moment(e.target.value).utc().format('YYYY-MM-DDTHH:mm'))}
                            min={moment().subtract(1, 'minute').format('YYYY-MM-DDTHH:mm')}
                            className="typo-small text-neutral px-2 py-1 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5"
                          />
                        ) : (
                          <span className="typo-small text-neutral">{moment.utc(rental.startDateUTC).local().format('DD/MM/YYYY HH:mm')}</span>
                        )}
                      </div>
                      <Row label="Fin">{moment.utc(rental.endDateUTC).local().format('DD/MM/YYYY HH:mm')}</Row>
                      <Row label="Durée">
                        <span className={duration === 'Annulé' ? 'text-error-500' : ''}>{duration}</span>
                      </Row>
                      {isNotStarted && (
                        <div className="flex justify-end gap-2 mt-1">
                          {isEditingDate ? (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => setIsEditingDate(false)}>
                                Annuler
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={saveDate}
                                disabled={updateStartDateMutation.isPending || !editStartDate}
                                icon={<PencilSquareIcon className="h-4 w-4" />}
                              >
                                Sauvegarder et notifier
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditStartDate(rental.startDateUTC);
                                setIsEditingDate(true);
                              }}
                              icon={<PencilSquareIcon className="h-4 w-4" />}
                            >
                              Modifier la date
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Pricing */}
                    <div className="flex flex-col gap-2 pt-4 border-t border-neutral-10">
                      <Row label="Prix">{(rental.price / 100).toFixed(2)} €</Row>
                      <Row label="Caution">{(rental.deposit / 100).toFixed(2)} €</Row>
                      {rental.invoice && (
                        <>
                          <Row label="Statut facturation">{rental.invoice.status}</Row>
                          {rental.invoice.finalPrice != null && (
                            <Row label="Prix final">{(rental.invoice.finalPrice / 100).toFixed(2)} €</Row>
                          )}
                        </>
                      )}
                      {isExtendable && (
                        <div className="flex justify-end mt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmExtendOpen(true)}
                            disabled={extendRentalMutation.isPending}
                          >
                            Prolonger
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Renter */}
                    <div className="flex flex-col gap-2 pt-4 border-t border-neutral-10">
                      <Row label="Loueur">{rental.user.firstName} {rental.user.lastName}</Row>
                      <Row label="Email">{rental.user.email}</Row>
                      <Row label="Téléphone">{rental.user.phone}</Row>
                      {hasEmergencyContact && (
                        <>
                          {(rental.user.sosFirstname || rental.user.sosLastname) && (
                            <Row label="Contact d'urgence">
                              {`${rental.user.sosFirstname ?? ''} ${rental.user.sosLastname ?? ''}`.trim()}
                            </Row>
                          )}
                          {rental.user.sosPhone && <Row label="Tél. d'urgence">{rental.user.sosPhone}</Row>}
                        </>
                      )}
                      {rental.comment && <Row label="Commentaire">{rental.comment}</Row>}
                    </div>

                    {/* History: status audit trail + station events, console-style table */}
                    <RentalHistoryTable events={events ?? []} stationEvents={stationEvents ?? []} />
                  </div>

                  {/* Actions */}
                  {(isCancellable || isClosable) && (
                    <div className="px-6 py-4 border-t border-neutral-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:justify-end">
                      {isCancellable && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setConfirmCancel(true)}
                          disabled={endRentalMutation.isPending}
                        >
                          Annuler la réservation
                        </Button>
                      )}
                      {isClosable && !isCancellable && isCash && (
                        <>
                          <div className="flex flex-col gap-1 sm:mr-auto">
                            <label className="flex items-center gap-2 typo-small text-neutral whitespace-nowrap">
                              Montant encaissé
                              <span className="relative inline-flex items-center">
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={cashAmount}
                                  onChange={(e) => setCashAmount(e.target.value)}
                                  disabled={isCapturing}
                                  className="w-24 pl-2 pr-6 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small"
                                />
                                <span className="pointer-events-none absolute right-2 text-neutral-40 typo-small">€</span>
                              </span>
                            </label>
                            <label className="flex items-center gap-2 typo-small text-neutral cursor-pointer select-none whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={includeDeposit}
                                onChange={(e) => setIncludeDeposit(e.target.checked)}
                                disabled={isCapturing}
                                className="h-4 w-4 rounded border-neutral-20"
                              />
                              {`Inclure la caution (${(rental.deposit / 100).toFixed(2)} €)`}
                            </label>
                            <span className="typo-small text-neutral-40">
                              Aucun paiement Stripe — marque la location comme encaissée (espèces).
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => closeRentalMutation.mutate(rental.id)}
                            disabled={isCapturing || closeRentalMutation.isPending}
                          >
                            Fermer sans facturer
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => capturePaymentMutation.mutate(rental.id)}
                            disabled={isCapturing}
                          >
                            {isCapturing ? 'Encaissement...' : `Encaisser ${(cashChargeCents / 100).toFixed(2)} € (espèces) et fermer`}
                          </Button>
                        </>
                      )}
                      {isClosable && !isCancellable && !isCash && (
                        <>
                          <label className="flex items-center gap-2 typo-small text-neutral cursor-pointer select-none whitespace-nowrap sm:mr-auto">
                            <input
                              type="checkbox"
                              checked={applyReferralDiscount}
                              onChange={(e) => setApplyReferralDiscount(e.target.checked)}
                              disabled={isCapturing}
                              className="h-4 w-4 rounded border-neutral-20"
                            />
                            {`Parrainage (-${discount} %)`}
                          </label>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => closeRentalMutation.mutate(rental.id)}
                            disabled={isCapturing || closeRentalMutation.isPending}
                          >
                            Fermer sans facturer
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => capturePaymentMutation.mutate(rental.id)}
                            disabled={isCapturing}
                          >
                            {isCapturing ? 'Facturation...' : `Facturer ${(captureAmount / 100).toFixed(2)} € et fermer`}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <ModalConfirmDelete
        visible={confirmCancel}
        title="Annuler la réservation ?"
        description="Cette action annule la réservation et libère l'équipement. Le client en sera informé."
        confirmLabel="Annuler la réservation"
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => {
          setConfirmCancel(false);
          endRentalMutation.mutate(rental.id);
        }}
      />

      <ModalExtendRental
        visible={confirmExtendOpen}
        isLoadingOptions={extendOptionsQuery.isLoading}
        options={extendOptionsQuery.data}
        isSubmitting={extendRentalMutation.isPending}
        onCancel={() => setConfirmExtendOpen(false)}
        onConfirm={(hours) => extendRentalMutation.mutate({ rentalId: rental.id, additionalHours: hours })}
      />

      <ModalOpenLock
        visible={equipmentToOpen != null}
        lock={equipmentToOpen?.lock ?? null}
        onCancel={() => setEquipmentToOpen(null)}
        onConfirm={() => {
          if (equipmentToOpen?.lockId) openLockMutation.mutate(equipmentToOpen.lockId);
        }}
        isLoading={openLockMutation.isPending}
      />

      <RentalEquipmentPicker
        visible={pickerEquipmentId != null}
        rentalId={rental.id}
        currentEquipmentId={pickerEquipmentId ?? undefined}
        priceEligible={isSwapPriceEligible}
        onClose={() => setPickerEquipmentId(null)}
        onSelect={(equipment: AvailableEquipment, updatePrice: boolean) => {
          if (pickerEquipmentId == null) return;
          changeEquipmentMutation.mutate({
            rentalId: rental.id,
            previousEquipmentId: pickerEquipmentId,
            newEquipmentId: equipment.id,
            updatePrice,
          });
        }}
      />
    </>
  );
};

export default RentalDetailsModal;
